package at.theatervogelweide.admin.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.ElementBuilder
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

private val dialog = Bem("admin-dialog")

private val DialogElement = ElementBuilder { document.createElement("dialog") as HTMLElement }

/**
 * A question in the page, as a modal `<dialog>`: it keeps focus inside and Escape cancels. [confirmLabel] does what
 * the question asks, [danger] marks it as destructive; the cancel button gets the initial focus.
 */
@Composable
fun AdminConfirmDialog(
    open: Boolean,
    id: String,
    title: String,
    message: String,
    confirmLabel: String,
    cancelLabel: String,
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
    danger: Boolean = false,
) {
    var element by remember { mutableStateOf<HTMLElement?>(null) }
    val currentOnCancel by rememberUpdatedState(onCancel)

    DisposableEffect(open, element) {
        val dialogElement = element?.asDynamic()
        if (dialogElement != null) {
            if (open && dialogElement.open != true) {
                dialogElement.showModal()
            } else if (!open && dialogElement.open == true) {
                dialogElement.close()
            }
        }
        onDispose { }
    }

    TagElement(DialogElement, {
        classes(dialog.block)
        id(id)
        attr("aria-labelledby", "$id-title")
        attr("aria-describedby", "$id-message")
        ref {
            element = it
            onDispose { element = null }
        }
        // Escape
        addEventListener("cancel") { event ->
            event.preventDefault()
            currentOnCancel()
        }
    }) {
        if (open) {
            H2({
                classes(dialog.element("title").className)
                id("$id-title")
            }) { Text(title) }
            P({
                classes(dialog.element("message").className)
                id("$id-message")
            }) { Text(message) }
            Div({ classes(dialog.element("actions").className) }) {
                AdminButton(onClick = onCancel, autofocus = true) { Text(cancelLabel) }
                AdminButton(variant = if (danger) ButtonVariant.DANGER else ButtonVariant.PRIMARY, onClick = onConfirm) {
                    Text(confirmLabel)
                }
            }
        }
    }
}
